import { Inject, Injectable } from '@angular/core';
import { SHORT_BRAND_NAME } from '@rd/core/tokens/rd-lib.token';
import { HermesAuthError } from '@rd/errors/hermes-auth-error';
import { NydusNetworkAuthError } from '@rd/errors/nydus-network-auth-error';
import * as Sentry from '@sentry/angular';

export interface ISentryService {
  handleError(err: any): void;
  manualError(...args: any[]): void;
  manualWarning(...args: any[]): void;
  manualNydusNetworkAuthError(
    error: NydusNetworkAuthError,
    ...args: any[]
  ): void;
  manualHermesAuthError(error: HermesAuthError, ...args: any[]): void;
  trackUser({ userId }: { userId: string }): void;
}

@Injectable()
export class SentryService implements ISentryService {
  constructor(@Inject(SHORT_BRAND_NAME) private shortBrandName: string) {}

  // Default error handler should print out console errors, while manually
  // triggered error methods should not.
  handleError(err: any): void {
    console.error(err);
    if (err instanceof Error) {
      Sentry.captureException(err);
      return;
    }
    Sentry.captureMessage(toMessage(err), 'error');
  }

  manualError(...args: any[]): void {
    const [first, ...rest] = args;
    const extras = rest.length ? { args: rest } : undefined;
    if (first instanceof Error) {
      Sentry.captureException(first, extras ? { extra: extras } : undefined);
      return;
    }
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      if (extras) scope.setExtras(extras);
      Sentry.captureMessage(toMessage(first));
    });
  }

  manualWarning(...args: any[]): void {
    const [first, ...rest] = args;
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      if (rest.length) scope.setExtras({ args: rest });
      Sentry.captureMessage(toMessage(first));
    });
  }

  manualHermesAuthError(authError: HermesAuthError, ...args: any[]): void {
    const errorLabel = `${
      authError.name
    }: [${this.shortBrandName.toUpperCase()}] ${authError.message}`;
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setFingerprint(toFingerprint(authError.fingerprint));
      scope.setExtras({
        metadata: authError.error?.metadata,
        ...(args.length ? { args } : {}),
      });
      Sentry.captureMessage(errorLabel);
    });
  }

  manualNydusNetworkAuthError(
    authError: NydusNetworkAuthError,
    ...args: any[]
  ): void {
    const errorLabel = `${
      authError.name
    }: [${this.shortBrandName.toUpperCase()}] ${authError.message}`;
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setFingerprint(toFingerprint(authError.fingerprint));
      scope.setExtras({
        errors: authError.errors,
        ...(args.length ? { args } : {}),
      });
      Sentry.captureMessage(errorLabel);
    });
  }

  trackUser({ userId }: { userId: string }): void {
    // Only track userId; name/email are PII.
    Sentry.setUser({ id: userId });
  }
}

function toMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function toFingerprint(fingerprint: unknown): string[] {
  if (Array.isArray(fingerprint)) return fingerprint.map(String);
  if (fingerprint == null) return [];
  return [String(fingerprint)];
}
